// Company page (decision 30.10.2026): master data and contacts (editable, optimistic locking), the company's deals,
// and its chronicle: mails, calls, notes, meetings and tasks of the company and its deals, newest first. Further
// addresses and the mails for invoices and orders feed the address book of all apps (M10). Admins merge duplicates
// (AB3, views/merge.ts); a merged company's page only names the one it went into. Contacts are added here and hidden
// when they leave the company (decision 01.11.2026): never deleted, shown again behind „N ausgeblendete zeigen“.
import { ApiError, type ChronicleItem, type Company, type CompanyAddress, type CompanyEmail, type CompanyList, type Contact } from "../api";
import { loadCompany, S } from "../state";
import { $, cleanText, emptyState, esc, fmtDate, fmtDT, fmtSize, toast } from "../util";
import { changeRequestCard } from "./antraege";
import { downloadAttachment, showOriginal } from "./original";
import { activityDetails, recordBox, taskBox } from "./tasks";
import { markenHtml } from "./volumen";

/** Pharma and tablets of a deal the client has loaded (the open ones). */
const dealMarks = (id: string) => {
  const deal = S.deals.get(id);
  return deal ? markenHtml({ pharma: deal.profil?.pharma ?? false, jahresvolumen: deal.jahresvolumen }) : "";
};

/** `id: null` on a list: a new entry. */
let editing: { kind: "company" } | { kind: "contact"; id: string | null } | { kind: CompanyList; id: string | null } | null = null;
/** The hidden contacts are shown (nicht mehr bei der Firma). */
let showHidden = false;
/** The address of the contact form belongs to another contact: who it is, and what was typed. */
let taken: { message: string; contact: { id: string; companyId: string | null; name: string | null; hidden: boolean } | null; draft: Record<string, unknown> } | null = null;

/** Another company opens: a form left open on the previous one is gone. */
export function resetFirmaEdit(): void {
  editing = null;
  showHidden = false;
  taken = null;
}
const expanded = new Set<string>();

const KIND: Record<ChronicleItem["kind"], string> = { mail: "Mail", note: "Notiz", call: "Anruf", meeting: "Termin", task: "Aufgabe erledigt" };

const COMPANY_FIELDS: [keyof Company, string, ("number" | "textarea")?][] = [
  ["addressExtra", "Zusatz"],
  ["street", "Straße"],
  ["zip", "PLZ"],
  ["city", "Ort"],
  ["country", "Land"],
  ["phone", "Telefon"],
  ["email", "Mail"],
  ["website", "Website"],
  ["vatId", "USt-IdNr."],
  ["commercialRegister", "Handelsregister"],
  ["industry", "Branche"],
  ["kind", "Typ"],
  ["employees", "Mitarbeiter", "number"],
  ["linkedin", "LinkedIn"],
  ["description", "Beschreibung", "textarea"],
  ["notes", "Notizen", "textarea"],
];

/** „Kontakt hinzufügen“: what is needed to write to and call someone. */
const NEW_CONTACT_FIELDS: [keyof Contact, string][] = [
  ["salutation", "Anrede"],
  ["name", "Name"],
  ["role", "Position"],
  ["email", "Mail"],
  ["phone", "Telefon"],
  ["mobile", "Mobil"],
];

const CONTACT_FIELDS: [keyof Contact, string][] = [
  ["name", "Name"],
  ["role", "Position"],
  ["email", "Mail"],
  ["phone", "Telefon"],
  ["mobile", "Mobil"],
  ["salutation", "Anrede"],
  ["letterSalutation", "Briefanrede"],
  ["linkedin", "LinkedIn"],
  ["city", "Ort"],
  ["country", "Land"],
  ["language", "Sprache"],
];

export const ADDRESS_KIND: Record<CompanyAddress["kind"], string> = { rechnung: "Rechnung", lieferung: "Lieferung", weitere: "Weitere" };
export const EMAIL_PURPOSE: Record<CompanyEmail["purpose"], string> = { rechnung: "Rechnung", bestellung: "Bestellung" };

const ADDRESS_FIELDS: [keyof CompanyAddress, string][] = [
  ["extra", "Zusatz"],
  ["street", "Straße"],
  ["zip", "PLZ"],
  ["city", "Ort"],
  ["country", "Land"],
];

/** One line: „Halle 3, Hafenstraße 1, 28195 Bremen, DE“, leaving out what is missing. */
export function addressLine(a: { extra: string | null; street: string | null; zip: string | null; city: string | null; country: string | null }): string {
  return [a.extra, a.street, [a.zip, a.city].filter(Boolean).join(" "), a.country].filter(Boolean).join(", ");
}

const link = (url: string | null) => (url ? `<a href="${esc(/^https?:\/\//.test(url) ? url : `https://${url}`)}" target="_blank" rel="noopener noreferrer">${esc(url)}</a>` : "");

function companyFacts(c: Company): string {
  const rows: [string, string][] = [
    ["Adresse", esc(addressLine({ ...c, extra: c.addressExtra }))],
    ["Telefon", esc(c.phone)],
    ["Mail", c.email ? `<a href="mailto:${esc(c.email)}">${esc(c.email)}</a>` : ""],
    ["Website", link(c.website)],
    ["USt-IdNr.", esc(c.vatId)],
    ["Handelsregister", esc(c.commercialRegister)],
    ["Branche", esc(c.industry)],
    ["Mitarbeiter", c.employees != null ? String(c.employees) : ""],
    ["LinkedIn", link(c.linkedin)],
  ];
  const facts = rows.filter(([, v]) => v).map(([k, v]) => `<dt>${k}</dt><dd>${v}</dd>`).join("");
  return `${facts ? `<dl class="facts">${facts}</dl>` : `<p class="muted">Keine Stammdaten.</p>`}
    ${c.description ? `<p class="pre">${esc(c.description)}</p>` : ""}${c.notes ? `<p class="pre"><b>Notizen:</b> ${esc(c.notes)}</p>` : ""}`;
}

/** `email`: a required mail address; `mail`: an optional one; `required`: text that must be there. */
function input(name: string, label: string, value: unknown, type?: "number" | "textarea" | "email" | "mail" | "required"): string {
  const v = value == null ? "" : String(value);
  const attrs = type === "number" ? 'type="number" min="0"' : type === "email" ? 'type="email" required' : type === "mail" ? 'type="email"' : type === "required" ? "required" : "";
  return `<label><span>${label}</span>${type === "textarea" ? `<textarea name="${name}" rows="3">${esc(v)}</textarea>` : `<input name="${name}" ${attrs} value="${esc(v)}">`}</label>`;
}

function select(name: string, label: string, value: string, options: Record<string, string>): string {
  return `<label><span>${label}</span><select name="${name}">${Object.entries(options)
    .map(([v, l]) => `<option value="${v}"${v === value ? " selected" : ""}>${l}</option>`)
    .join("")}</select></label>`;
}

const buttons = (deletable = false) =>
  `<div class="fbtn"><button class="btn primary">Speichern</button><button class="btn" type="button" data-cancel>Abbrechen</button>${deletable ? `<button class="btn" type="button" data-delete>Löschen</button>` : ""}</div>`;

function companyForm(c: Company): string {
  // Id and version as when the form opened: saving never lands on another company or over a change made meanwhile.
  return `<form class="fform" data-edit="company" data-id="${esc(c.id)}" data-version="${c.version}">
    ${input("name", "Name", c.name)}${input("domains", "Domains (durch Komma getrennt)", c.domains.join(", "))}
    ${COMPANY_FIELDS.map(([k, label, type]) => input(k, label, c[k], type)).join("")}
    ${buttons()}</form>`;
}

/** A form of a list entry; without an entry, a new one. It keeps the company, the entry and its version. */
function itemForm(list: CompanyList, companyId: string, item: CompanyAddress | CompanyEmail | null, fields: string): string {
  return `<form class="fform" data-edit="${list}" data-company="${esc(companyId)}" data-id="${esc(item?.id ?? "")}" data-version="${item?.version ?? ""}">${fields}${buttons(!!item)}</form>`;
}

/** Admins merge duplicates (AB3); not while a form is open. */
const canMerge = () => !!S.me?.isAdmin && !editing;

function addressSection(companyId: string, addresses: CompanyAddress[], ro: boolean): string {
  const form = (a: CompanyAddress | null) =>
    itemForm(
      "addresses",
      companyId,
      a,
      `${select("kind", "Art", a?.kind ?? "rechnung", ADDRESS_KIND)}${input("label", "Bezeichnung", a?.label)}${ADDRESS_FIELDS.map(([k, label]) => input(k, label, a?.[k])).join("")}`,
    );
  const item = (a: CompanyAddress) =>
    editing?.kind === "addresses" && editing.id === a.id
      ? `<li>${form(a)}</li>`
      : `<li><div class="kh"><b>${ADDRESS_KIND[a.kind]}</b>${a.label ? ` <span class="muted">${esc(a.label)}</span>` : ""}${ro || editing ? "" : ` <button class="link" data-edit-item="addresses" data-id="${esc(a.id)}">Bearbeiten</button>`}${canMerge() && addresses.length > 1 ? ` <button class="link" data-merge-address="${esc(a.id)}">Zusammenführen …</button>` : ""}</div>
        <div>${esc(addressLine(a))}</div></li>`;
  const adding = editing?.kind === "addresses" && editing.id === null;
  return `<div class="sec"><h3>Adressen ${ro || editing ? "" : `<button class="btn" data-add-item="addresses">Hinzufügen</button>`}</h3>
    ${addresses.length ? `<ul class="contacts">${addresses.map(item).join("")}</ul>` : adding ? "" : `<p class="muted">Keine weiteren Adressen.</p>`}${adding ? form(null) : ""}</div>`;
}

function emailSection(companyId: string, emails: CompanyEmail[], ro: boolean): string {
  const form = (e: CompanyEmail | null) =>
    itemForm("emails", companyId, e, `${select("purpose", "Zweck", e?.purpose ?? "rechnung", EMAIL_PURPOSE)}${input("email", "Adresse", e?.email, "email")}${input("label", "Bezeichnung", e?.label)}`);
  const item = (e: CompanyEmail) =>
    editing?.kind === "emails" && editing.id === e.id
      ? `<li>${form(e)}</li>`
      : `<li><div class="kh"><b>${EMAIL_PURPOSE[e.purpose]}</b>${e.label ? ` <span class="muted">${esc(e.label)}</span>` : ""}${ro || editing ? "" : ` <button class="link" data-edit-item="emails" data-id="${esc(e.id)}">Bearbeiten</button>`}</div>
        <div><a class="mono" href="mailto:${esc(e.email)}">${esc(e.email)}</a></div></li>`;
  const adding = editing?.kind === "emails" && editing.id === null;
  return `<div class="sec"><h3>E-Mails für Rechnung und Bestellung ${ro || editing ? "" : `<button class="btn" data-add-item="emails">Hinzufügen</button>`}</h3>
    ${emails.length ? `<ul class="contacts">${emails.map(item).join("")}</ul>` : adding ? "" : `<p class="muted">Keine eigenen Adressen für Rechnung oder Bestellung.</p>`}${adding ? form(null) : ""}</div>`;
}

/** Whose the address is: on this page it shows that contact, else it opens the company it belongs to. */
function takenNote(companyId: string): string {
  if (!taken) return "";
  const c = taken.contact;
  const open = !c ? "" : c.companyId === companyId ? ` <button class="link" type="button" data-show-contact="${esc(c.id)}">Zeigen</button>` : c.companyId ? ` <a href="#firma-${esc(c.companyId)}">Öffnen</a>` : "";
  return `<p class="note warn">${esc(taken.message)}${open}</p>`;
}

/** What the form shows: typed values kept across a refused save, else the contact's. */
const shown = (k: Partial<Contact>, f: keyof Contact) => (taken && f in taken.draft ? taken.draft[f] : k[f]);

function contactForm(companyId: string, k: Contact | null): string {
  const fields = k ? CONTACT_FIELDS : NEW_CONTACT_FIELDS;
  return `<form class="fform" data-edit="contact" data-company="${esc(companyId)}" data-id="${esc(k?.id ?? "")}" data-version="${k?.version ?? ""}">
      ${fields.map(([f, label]) => input(f, label, shown(k ?? {}, f), f === "email" ? "mail" : f === "name" && !k ? "required" : undefined)).join("")}
      ${takenNote(companyId)}
      <div class="fbtn"><button class="btn primary">${k ? "Speichern" : "Hinzufügen"}</button><button class="btn" type="button" data-cancel>Abbrechen</button></div></form>`;
}

function contactItem(companyId: string, k: Contact, ro: boolean): string {
  if (editing?.kind === "contact" && editing.id === k.id) return `<li>${contactForm(companyId, k)}</li>`;
  const phones = [k.phone, k.mobile && k.mobile !== k.phone ? `${k.mobile} (mobil)` : ""].filter(Boolean).join(" · ");
  const extra = Object.entries(k.extra ?? {});
  const actions = ro || editing ? "" : ` <button class="link" data-edit-contact="${esc(k.id)}">Bearbeiten</button> <button class="link" data-hide-contact="${esc(k.id)}" data-version="${k.version}" title="Nicht mehr bei der Firma: aus der Liste und der Auswahl am Deal nehmen. Gelöscht wird nichts.">Ausblenden</button>`;
  return `<li data-contact="${esc(k.id)}"><div class="kh"><b>${esc(k.name ?? k.email ?? "Ohne Namen")}</b>${k.role ? ` <span class="muted">${esc(k.role)}</span>` : ""}${actions}${canMerge() ? ` <button class="link" data-merge-contact="${esc(k.id)}">Zusammenführen …</button>` : ""}</div>
    ${k.email ? `<div><a class="mono" href="mailto:${esc(k.email)}">${esc(k.email)}</a></div>` : ""}${phones ? `<div class="mono">${esc(phones)}</div>` : ""}
    ${k.letterSalutation ? `<div class="muted">„${esc(k.letterSalutation)}“</div>` : ""}
    ${extra.length ? `<dl class="facts small">${extra.map(([l, v]) => `<dt>${esc(l)}</dt><dd>${esc(v)}</dd>`).join("")}</dl>` : ""}</li>`;
}

/** A contact no longer with the company: name and how to reach it as it was, and the way back. */
function hiddenItem(k: Contact, ro: boolean): string {
  const by = k.hiddenBy ? ` von ${esc(k.hiddenBy)}` : "";
  return `<li data-contact="${esc(k.id)}"><div class="kh"><b>${esc(k.name ?? k.email ?? "Ohne Namen")}</b>${k.role ? ` <span class="muted">${esc(k.role)}</span>` : ""}${ro || editing ? "" : ` <button class="link" data-unhide-contact="${esc(k.id)}" data-version="${k.version}">Wieder einblenden</button>`}</div>
    ${k.email ? `<div class="mono muted">${esc(k.email)}</div>` : ""}<div class="muted">Nicht mehr bei der Firma · ausgeblendet am ${fmtDate(k.hiddenAt!)}${by}</div></li>`;
}

export function contactSection(companyId: string, contacts: Contact[], ro: boolean): string {
  const visible = contacts.filter((k) => !k.hiddenAt);
  const hidden = contacts.filter((k) => k.hiddenAt);
  const adding = editing?.kind === "contact" && editing.id === null;
  const toggle = hidden.length
    ? `<p><button class="link" data-toggle-hidden>${showHidden ? "Ausgeblendete verbergen" : `${hidden.length} ausgeblendete zeigen`}</button></p>${showHidden ? `<ul class="contacts">${hidden.map((k) => hiddenItem(k, ro)).join("")}</ul>` : ""}`
    : "";
  return `<div class="sec"><h3>Kontakte <small>${visible.length}</small> ${ro || editing ? "" : `<button class="btn" data-add-contact>Kontakt hinzufügen</button>`}</h3>
    ${adding ? contactForm(companyId, null) : ""}
    ${visible.length ? `<ul class="contacts">${visible.map((k) => contactItem(companyId, k, ro)).join("")}</ul>` : adding ? "" : `<p class="muted">Keine Kontakte.</p>`}${toggle}</div>`;
}

function chronicleItem(i: ChronicleItem): string {
  const body = cleanText(i.body);
  const planned = Date.parse(i.at) > Date.now() && !i.cancelled;
  const long = body.length > 280 || body.split("\n").length > 5;
  const open = expanded.has(i.id);
  const who = i.kind === "mail" ? `${i.direction === "out" ? "an Kunde" : esc(i.from ?? "")}` : esc(i.author ?? "");
  const atts = i.attachments.length
    ? `<ul class="atts">${i.attachments
        .map(
          (a) =>
            `<li><button class="link" data-att="${esc(a.id)}" data-name="${esc(a.filename ?? "anhang")}">📎 ${esc(a.filename ?? "Anhang")}</button>${a.sizeBytes ? ` <span class="muted">${fmtSize(a.sizeBytes)}</span>` : ""}${a.summary ? `<p>${esc(a.summary)}</p>` : ""}</li>`,
        )
        .join("")}</ul>`
    : "";
  return `<li class="act ${long && !open ? "clamp" : ""}"><div class="mh"><span><span class="dir">${KIND[i.kind]}</span> ${who}${i.dealRef ? ` · <a class="mono" href="#deal-${esc(i.dealId)}">${esc(i.dealRef)}</a>` : ""}</span><span class="mono">${planned ? `<span class="pill p-info">geplant</span> ` : ""}${fmtDT(i.at)}</span></div>
    ${i.title ? `<div class="ms">${esc(i.title)}</div>` : ""}${i.kind === "mail" ? "" : activityDetails({ ...i, type: i.kind })}${body ? `<div class="act-b">${esc(body)}</div>` : ""}
    ${long ? `<button class="link" data-more="${esc(i.id)}">${open ? "Weniger" : "Ganzen Text"}</button>` : ""}${atts}
    ${i.hasOriginal ? `<div><button class="link" data-original="${esc(i.id)}">Original</button></div>` : ""}</li>`;
}

/** `force`: also when a form is open (after saving); the 60-second refresh leaves an open form alone. */
export function renderFirma(force = false): void {
  const el = $("#v-firma");
  // An open form stays while typing, but only for the company it belongs to.
  if (!force && editing && el.querySelector("form[data-edit]") && S.company?.company.id === S.companyId) return;
  const d = S.company;
  if (!d || d.company.id !== S.companyId) {
    el.innerHTML = emptyState("Lade Firma …", "");
    return;
  }
  const c = d.company;
  const ro = S.readOnly;
  // An old link to a merged duplicate: only where everything is now.
  if (d.mergedInto) {
    el.innerHTML = `<div class="fhead"><a href="#firmen" class="muted">← Firmen</a><h2>${esc(c.name)}</h2></div>
      <p class="note">Zusammengeführt in <a href="#firma-${esc(d.mergedInto.id)}">${esc(d.mergedInto.name)}</a>. Deals, Personen, Mails und Adressen stehen jetzt dort.</p>`;
    return;
  }
  el.innerHTML = `
    <div class="fhead"><a href="#firmen" class="muted">← Firmen</a><h2>${esc(c.name)}</h2>
      <div class="chips">${c.kind ? `<span class="chip">${esc(c.kind)}</span>` : ""}${c.domains.map((x) => `<span class="chip mono">${esc(x)}</span>`).join("")}${c.fromHubSpot ? `<span class="muted">aus HubSpot übernommen</span>` : ""}${canMerge() ? `<button class="link merge-co" data-merge-company>Mit anderer Firma zusammenführen …</button>` : ""}</div></div>
    ${d.changeRequests.length ? `<div class="sec crs"><h3>Änderungsanträge des Kunden <small>${d.changeRequests.length}</small></h3>${d.changeRequests.map((r) => changeRequestCard(r, false)).join("")}</div>` : ""}
    <div class="fgrid">
      <div>
        <div class="sec"><h3>Stammdaten ${ro || editing ? "" : `<button class="btn" data-edit-company>Bearbeiten</button>`}</h3>${editing?.kind === "company" ? companyForm(c) : companyFacts(c)}</div>
        ${addressSection(c.id, d.addresses, ro)}
        ${emailSection(c.id, d.emails, ro)}
        ${contactSection(c.id, d.contacts, ro)}
        <div class="sec"><h3>Deals <small>${d.deals.length}</small></h3>${
          d.deals.length
            ? `<ul class="fdeals">${d.deals.map((x) => `<li><a href="#deal-${esc(x.id)}"><span class="mono">${esc(x.ref)}</span> ${esc(x.name)}</a>${dealMarks(x.id)} <span class="muted">${esc(x.stage)}${x.offerStage ? ` · ${esc(x.offerStage)}` : ""}</span></li>`).join("")}</ul>`
            : `<p class="muted">Keine Deals.</p>`
        }</div>
      </div>
      <div>
        <div class="sec"><h3>Aufgaben <small>${d.tasks.length ? `${d.tasks.length} offen` : ""}</small></h3>
          ${taskBox({ companyId: c.id }, d.tasks, { where: true })}
          ${recordBox({ companyId: c.id }, d.contacts.filter((k) => !k.hiddenAt))}
        </div>
        <div class="sec"><h3>Chronik <small>${d.chronicle.length} · neueste zuerst</small></h3>${
          d.chronicle.length ? `<ul class="acts">${d.chronicle.map(chronicleItem).join("")}</ul>` : emptyState("Noch nichts", "Mails, Anrufe, Notizen und Termine der Firma erscheinen hier.")
        }</div>
      </div>
    </div>`;
}

/** Form values: empty is null (the company's name then stays), domains as a list, employees as a number. */
export function formValues(edit: string, entries: Iterable<[string, FormDataEntryValue]>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, raw] of entries) {
    const v = String(raw).trim();
    if (k === "domains") out[k] = v.split(/[,\s]+/).map((x) => x.toLowerCase()).filter(Boolean);
    else if (k === "employees") out[k] = v ? Number(v) : null;
    else out[k] = v || (k === "name" && edit === "company" ? undefined : null);
  }
  return out;
}
const values = (form: HTMLFormElement) => formValues(form.dataset.edit!, new FormData(form).entries());

const NOUN: Record<CompanyList, string> = { addresses: "Adresse", emails: "Mailadresse" };

/** `draft`: the typed values, kept when the address turns out to be another contact's. */
async function save(run: () => Promise<unknown>, okMsg: string, draft: Record<string, unknown> = {}): Promise<void> {
  try {
    await run();
    editing = null;
    taken = null;
    toast(okMsg);
  } catch (err) {
    if (err instanceof ApiError && err.code === "email_taken") {
      taken = { message: err.message, contact: (err.details.contact as NonNullable<typeof taken>["contact"]) ?? null, draft };
      return renderFirma(true);
    }
    if (err instanceof ApiError && err.status === 409) {
      editing = null;
      toast("Inzwischen geändert. Die Ansicht ist neu geladen, bitte noch einmal.");
    } else {
      toast(err instanceof ApiError ? err.message : "Speichern fehlgeschlagen. Bitte erneut versuchen.");
      return;
    }
  }
  await loadCompany().catch(() => undefined);
  renderFirma(true);
}

export function bindFirma(): void {
  document.addEventListener("click", (e) => {
    const t = (e.target as HTMLElement).closest<HTMLElement>(
      "#v-firma [data-edit-company], #v-firma [data-edit-contact], #v-firma [data-add-contact], #v-firma [data-hide-contact], #v-firma [data-unhide-contact], #v-firma [data-toggle-hidden], #v-firma [data-show-contact], #v-firma [data-add-item], #v-firma [data-edit-item], #v-firma [data-delete], #v-firma [data-cancel], #v-firma [data-more], #v-firma [data-att], #v-firma [data-original]",
    );
    if (!t) return;
    taken = t.dataset.showContact ? taken : null;
    if (t.dataset.editCompany !== undefined) editing = { kind: "company" };
    else if (t.dataset.editContact) editing = { kind: "contact", id: t.dataset.editContact };
    else if (t.dataset.addContact !== undefined) editing = { kind: "contact", id: null };
    else if (t.dataset.hideContact) return void save(() => S.api.updateContact(t.dataset.hideContact!, Number(t.dataset.version), { hidden: true }), "Ausgeblendet: nicht mehr bei der Firma. Nichts ist gelöscht.");
    else if (t.dataset.unhideContact) return void save(() => S.api.updateContact(t.dataset.unhideContact!, Number(t.dataset.version), { hidden: false }), "Wieder eingeblendet");
    else if (t.dataset.toggleHidden !== undefined) showHidden = !showHidden;
    else if (t.dataset.showContact) {
      // The address is held by a contact of this company: show it (behind the toggle when hidden) and leave the form.
      const id = t.dataset.showContact;
      if (S.company?.contacts.some((k) => k.id === id && k.hiddenAt)) showHidden = true;
      editing = null;
      taken = null;
      renderFirma(true);
      return void document.querySelector(`#v-firma li[data-contact="${CSS.escape(id)}"]`)?.scrollIntoView({ block: "center" });
    }
    else if (t.dataset.addItem) editing = { kind: t.dataset.addItem as CompanyList, id: null };
    else if (t.dataset.editItem) editing = { kind: t.dataset.editItem as CompanyList, id: t.dataset.id! };
    else if (t.dataset.delete !== undefined) {
      const f = t.closest<HTMLFormElement>("form[data-edit]")!;
      const list = f.dataset.edit as CompanyList;
      return void save(() => S.api.deleteCompanyItem(f.dataset.company!, list, f.dataset.id!, Number(f.dataset.version)), `${NOUN[list]} gelöscht`);
    } else if (t.dataset.cancel !== undefined) editing = null;
    else if (t.dataset.more) {
      if (!expanded.delete(t.dataset.more)) expanded.add(t.dataset.more);
    } else if (t.dataset.att) return void downloadAttachment(t.dataset.att, t.dataset.name ?? "anhang");
    else if (t.dataset.original) return void showOriginal(t.dataset.original);
    renderFirma(true);
  });
  document.addEventListener("submit", (e) => {
    const f = e.target as HTMLFormElement;
    if (!f.matches("#v-firma form[data-edit]")) return;
    e.preventDefault();
    if (f.dataset.edit === "company") void save(() => S.api.updateCompany(f.dataset.id!, Number(f.dataset.version), values(f)), "Stammdaten gespeichert");
    if (f.dataset.edit === "contact") {
      const v = values(f);
      if (f.dataset.id) void save(() => S.api.updateContact(f.dataset.id!, Number(f.dataset.version), v), "Kontakt gespeichert", v);
      else void save(() => S.api.addContact(f.dataset.company!, v), "Kontakt hinzugefügt", v);
    }
    if (f.dataset.edit === "addresses" || f.dataset.edit === "emails") {
      const list = f.dataset.edit;
      const { company, id, version } = f.dataset;
      void save(() => (id ? S.api.updateCompanyItem(company!, list, id, Number(version), values(f)) : S.api.addCompanyItem(company!, list, values(f))), `${NOUN[list]} gespeichert`);
    }
  });
}
